'use client';
import logo from '@/public/files/sms-webinar-logo.png';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setLogo, setFooterText, setPageLoading, setEditor, setRecaptcha, setSiteStatus } from '@/components/default/redux/slices/layoutSlice';
import LayoutService from '@/components/default/services/api/layout.service';
import {
    setMainDomain,
    setIsValidMainDomain,
    setSubdomain,
    setIsValidSubDomain,
    setCustomDomain,
    setIsValidCustomDomain,
    setDomainType,
    setDomain,
} from '@/components/default/redux/slices/domainSlice';
import StorageService from '../../services/app/storage.service';
import { loadSubscription } from '../../redux/slices/subscriptionSlice';

export default function LayoutInitializer({
    children,
}: Readonly<{
    children: React.ReactNode;
}>) {
    // This component initializes the layout by checking the domain and setting the logo and footer text
    const dispatch = useDispatch(); // Use useDispatch to get the dispatch function from Redux store
    // const dispatch = useDispatch<AppDispatch>(); // Use AppDispatch type if you have it defined
    // const dispatch = useDispatch<Dispatch>(); // Use Dispatch type if you have it defined
    const mainDomain = process.env.NEXT_PUBLIC_APP_DOMAIN ?? ''; // Ensure this is set in your environment variables
    // const mainDomain = "mofassel.com"; // For testing purposes, you can hardcode the domain here
    const mainParts = mainDomain.split('.'); // Extract the prefix from the domain ['mofassel', 'com']
    // const mainParts = "mofassel"; // For testing purposes, you can hardcode the prefix here
    // const mainParts = "localhost"; // For testing purposes, you can hardcode the prefix her
    // const mainParts = "app"; // For testing purposes, you can hardcode the prefix here
    // const mainParts = "api"; // For testing purposes, you can hardcode the prefix here
    // const mainParts = "admin"; // For testing purposes, you can hardcode the prefix here
    // const mainParts = "www"; // For testing purposes, you can hardcode the prefix here
    // const mainParts = "reviewengineer"; // For testing purposes, you can hardcode the prefix here
    // const mainParts = "selling-machine-secrets-frontend"; // For testing purposes, you can hardcode the prefix here

    const yearStart = 2018;
    const currentYear = new Date().getFullYear();
    const website = mainDomain.toUpperCase(); // Convert the main domain to uppercase for display
    const footerText = `Copyright © ${yearStart}-${currentYear} | ${website} | All Rights Reserved`;

    // Check if the main domain prefix is set correctly
    useEffect(() => {
        // console.log(`Main domain: ${mainDomain}`);
        // console.log(`Main domain prefix: ${mainParts}`);
        // Check if the main domain is set correctly
        if (!mainDomain) {
            // console.error("Main domain is not set in environment variables.");
            // dispatch(setIsSetMainDomain(false)); // Set main domain validity to false if no valid main domain is detected
        }
        const hostname = window.location.hostname; // e.g., sub.mofassel.com or anotherdomain.com
        // console.log("Host name:", hostname); // e.g., sub.mofassel.com or anotherdomain.com
        const hostParts = hostname.split('.');
        // console.log("Host parts:", hostParts); // e.g., ["sub", "mofassel", "com"] ["mofassel", "com"] ["anotherdomain", "com"]
        // Check if the hostname is localhost or an IP address
        const isLocalhost = hostname === 'localhost' || hostname.startsWith('localhost:');
        const isIP = /^[\d.]+$/.test(hostname);

        // Check if hostname ends with the main domain
        const endsWithMainDomain = hostParts.slice(-mainParts.length).join('.') === mainDomain;

        // If it ends with the main domain and has something before, it's a subdomain
        const hasSubdomain = endsWithMainDomain && hostParts.length > mainParts.length;

        const subdomain = hasSubdomain ? hostParts.slice(0, hostParts.length - mainParts.length).join('.') : null;

        const isValidSubdomain = !!subdomain && /^[a-zA-Z0-9-]+$/.test(subdomain);

        const isMainDomain = endsWithMainDomain && !hasSubdomain;

        const isCustomDomain = !isLocalhost && !isIP && !endsWithMainDomain;

        // console.log("Hostname:", hostname);
        // console.log("Main domain:", mainDomain);
        // console.log("Ends with main domain:", endsWithMainDomain);
        // console.log("Is main domain:", isMainDomain);
        // console.log("Subdomain:", subdomain);
        // console.log("Is valid subdomain:", isValidSubdomain);
        // console.log("Is custom domain:", isCustomDomain);

        // Check if the main domain is valid
        if (isLocalhost) {
            const existDomain = StorageService.get('domain');
            const existDomainType = StorageService.get('domain-type');
            if (existDomain && existDomainType) {
                // console.log('existDomain',existDomain)
                // console.log('existDomainType',existDomainType)
                // Set cookies that will be sent on server requests
                document.cookie = `domain=${existDomain}; path=/; samesite=lax`;
                document.cookie = `domain-type=${existDomainType}; path=/; samesite=lax`;
            }

            if (existDomain && existDomain != 'localhost') {
                dispatch(setSubdomain(existDomain)); // Set subdomain
                dispatch(setDomain(existDomain)); // Set domain
                LayoutService.checkSubAccount(existDomain)
                    .then((result) => {
                        //   console.log("Result:", result);
                        if (result.success) {
                            //   console.log("Subdomain is valid.");
                            dispatch(setIsValidSubDomain(true)); // Set subdomain validity to true
                            if (result.data.layout_settings.header.logo) {
                                dispatch(setLogo(result.data.layout_settings.header.logo || logo.src)); // Use the logo from the API or fallback to the default logo
                            }
                            if (result.data.layout_settings.footer.text) {
                                dispatch(setFooterText(result.data.layout_settings.footer.text || footerText)); // Use the footer text from the API or fallback to the default text
                            }
                            if (result.data.layout_settings.editor) {
                                dispatch(setEditor(result.data.layout_settings.editor || 'quill')); // Use the footer text from the API or fallback to the default text
                            }
                            if (result.data.layout_settings.recaptcha) {
                                dispatch(setRecaptcha(result.data.layout_settings.recaptcha || false)); // Use the footer text from the API or fallback to the default text
                            }
                            dispatch(setSiteStatus(result.data.layout_settings.status)); // Use the footer text from the API or fallback to the default text
                        } else {
                            //   console.log("Subdomain is not valid.");
                            dispatch(setIsValidSubDomain(false)); // Set subdomain validity to false if no valid subdomain is detected
                        }
                    })
                    .catch((error) => {
                        // console.error(error);
                        dispatch(setSubdomain(subdomain)); // Set subdomain
                        dispatch(setIsValidSubDomain(false)); // Set subdomain validity to true
                    })
                    .finally(() => {
                        dispatch(setPageLoading(true)); // Set page loading to true after fetching the logo and footer text
                    });
                // All will false if sub domain is valid
                dispatch(setMainDomain(null)); // Set main domain to null if no valid main domain is detected
                dispatch(setIsValidMainDomain(false)); // Set main domain validity to false if no valid main domain is detected
                dispatch(setCustomDomain(null)); // Set custom domain validity to true
                dispatch(setIsValidCustomDomain(false)); // Set custom domain validity to true
                dispatch(setDomainType('sub')); // Set domain type to root
            } else {
                StorageService.set('domain', mainDomain);
                StorageService.set('domain-type', 'main');
                // console.log("Main domain is valid.");
                LayoutService.index()
                    .then((result) => {
                        if (result.data.header.logo) {
                            dispatch(setLogo(result.data.header.logo || logo.src)); // Use the logo from the API or fallback to the default logo
                        }
                        if (result.data.footer.text) {
                            dispatch(setFooterText(result.data.footer.text || footerText)); // Use the footer text from the API or fallback to the default text
                        }
                        if (result.data.editor) {
                            dispatch(setEditor(result.data.editor || 'quill')); // Use the footer text from the API or fallback to the default text
                        }
                        if (result.data.recaptcha) {
                            dispatch(setRecaptcha(result.data.recaptcha || false)); // Use the footer text from the API or fallback to the default text
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                        dispatch(setLogo(logo.src)); // logo is an object with a 'src' property
                        dispatch(setFooterText(footerText));
                    })
                    .finally(() => {
                        dispatch(setPageLoading(true)); // Set page loading to true after fetching the logo and footer text
                    });
                // console.log("Localhost is valid.");
                dispatch(setMainDomain(mainDomain)); // Set main domain
                dispatch(setDomain(mainDomain)); // Set domain
                dispatch(setIsValidMainDomain(true)); // Set main domain validity to true
                dispatch(setSubdomain(null)); // Set subdomain to null if no valid subdomain is detected
                dispatch(setIsValidSubDomain(false)); // Set subdomain validity to false if no valid subdomain is detected
                dispatch(setCustomDomain(null)); // Set custom domain validity to true
                dispatch(setIsValidCustomDomain(false)); // Set custom domain validity to true
                dispatch(setDomainType('main')); // Set domain type to root`
                // dispatch(setPageLoading(true)); // Set page loading to true after fetching the logo and footer text
            }
        } else if (isMainDomain) {
            StorageService.set('domain', mainDomain);
            StorageService.set('domain-type', 'main');
            // console.log("Main domain is valid.");
            LayoutService.index()
                .then((result) => {
                    if (result.data.header.logo) {
                        dispatch(setLogo(result.data.header.logo || logo.src)); // Use the logo from the API or fallback to the default logo
                    }
                    if (result.data.footer.text) {
                        dispatch(setFooterText(result.data.footer.text || footerText)); // Use the footer text from the API or fallback to the default text
                    }
                    if (result.data.editor) {
                        dispatch(setEditor(result.data.editor || null)); // Use the footer text from the API or fallback to the default text
                    }
                    if (result.data.recaptcha) {
                        dispatch(setRecaptcha(result.data.recaptcha || false)); // Use the footer text from the API or fallback to the default text
                    }
                    // dispatch(setPageLoading(true)); // Set page loading to true after fetching the logo and footer text
                })
                .catch((error) => {
                    console.error(error);
                    dispatch(setLogo(logo.src)); // logo is an object with a 'src' property
                    dispatch(setFooterText(footerText));
                    // dispatch(setPageLoading(true)); // Set page loading to true after fetching the logo and footer text
                })
                .finally(() => {
                    dispatch(setPageLoading(true)); // Set page loading to true after fetching the logo and footer text
                });
            // Set main domain validity to true if no valid main domain is detected
            dispatch(setMainDomain(mainDomain)); // Set main domain
            dispatch(setDomain(mainDomain)); // Set domain
            dispatch(setIsValidMainDomain(true)); // Set main domain validity to true
            // all will false if main domain is valid
            dispatch(setSubdomain(null)); // Set subdomain to null if no valid subdomain is detected
            dispatch(setIsValidSubDomain(false)); // Set subdomain validity to false if no valid subdomain is detected
            dispatch(setCustomDomain(null)); // Set custom domain validity to true
            dispatch(setIsValidCustomDomain(false)); // Set custom domain validity to true
            dispatch(setDomainType('main')); // Set domain type to root
        } else if (isValidSubdomain) {
            StorageService.set('domain', subdomain);
            StorageService.set('domain-type', 'sub');
            dispatch(setSubdomain(subdomain)); // Set subdomain
            dispatch(setDomain(subdomain)); // Set domain
            LayoutService.checkSubAccount(subdomain)
                .then((result) => {
                    //   console.log("Result:", result);
                    if (result.success) {
                        //   console.log("Subdomain is valid.");
                        dispatch(setIsValidSubDomain(true)); // Set subdomain validity to true
                        if (result.data.layout_settings.header.logo) {
                            dispatch(setLogo(result.data.layout_settings.header.logo || logo.src)); // Use the logo from the API or fallback to the default logo
                        }
                        if (result.data.layout_settings.footer.text) {
                            dispatch(setFooterText(result.data.layout_settings.footer.text || footerText)); // Use the footer text from the API or fallback to the default text
                        }
                        if (result.data.layout_settings.editor) {
                            dispatch(setEditor(result.data.layout_settings.editor || 'quill')); // Use the footer text from the API or fallback to the default text
                        }
                        if (result.data.layout_settings.recaptcha) {
                            dispatch(setRecaptcha(result.data.layout_settings.recaptcha || false)); // Use the footer text from the API or fallback to the default text
                        }
                        if (result.data.subscription) {
                            dispatch(loadSubscription(result.data.subscription));
                        }
                    } else {
                        //   console.log("Subdomain is not valid.");
                        dispatch(setIsValidSubDomain(false)); // Set subdomain validity to false if no valid subdomain is detected
                    }
                })
                .catch((error) => {
                    // console.error(error);
                    dispatch(setSubdomain(subdomain)); // Set subdomain
                    dispatch(setIsValidSubDomain(false)); // Set subdomain validity to true
                })
                .finally(() => {
                    dispatch(setPageLoading(true)); // Set page loading to true after fetching the logo and footer text
                });
            // All will false if sub domain is valid
            dispatch(setMainDomain(null)); // Set main domain to null if no valid main domain is detected
            dispatch(setIsValidMainDomain(false)); // Set main domain validity to false if no valid main domain is detected
            dispatch(setCustomDomain(null)); // Set custom domain validity to true
            dispatch(setIsValidCustomDomain(false)); // Set custom domain validity to true
            dispatch(setDomainType('sub')); // Set domain type to root
        } else if (isCustomDomain) {
            StorageService.set('domain', hostname);
            StorageService.set('domain-type', 'custom');
            LayoutService.checkCustomAccount(hostname)
                .then((result) => {
                    //   console.log("Result:", result);
                    // Check if the result is valid
                    if (result.success) {
                        //   console.log("Custom domain is valid.");
                        dispatch(setIsValidCustomDomain(true)); // Set custom domain validity to true

                        if (result.data.layout_settings.header.logo) {
                            dispatch(setLogo(result.data.layout_settings.header.logo || logo.src)); // Use the logo from the API or fallback to the default logo
                        }
                        if (result.data.layout_settings.footer.text) {
                            dispatch(setFooterText(result.data.layout_settings.footer.text || footerText)); // Use the footer text from the API or fallback to the default text
                        }
                        if (result.data.layout_settings.editor) {
                            dispatch(setEditor(result.data.layout_settings.editor || 'quill')); // Use the footer text from the API or fallback to the default text
                        }
                        if (result.data.layout_settings.recaptcha) {
                            dispatch(setRecaptcha(result.data.layout_settings.recaptcha || false)); // Use the footer text from the API or fallback to the default text
                        }
                        if (result.data.subscription) {
                            dispatch(loadSubscription(result.data.subscription));
                        }
                    } else {
                        dispatch(setIsValidCustomDomain(false));
                    }
                })
                .catch((error) => {
                    dispatch(setIsValidCustomDomain(false));
                })
                .finally(() => {
                    dispatch(setPageLoading(true)); // Set page loading to true after fetching the logo and footer text
                });
            dispatch(setCustomDomain(hostname)); // Set subdomain to null if no valid subdomain is detected
            dispatch(setDomain(hostname)); // Set domain
            // All will false if custom domain is valid
            dispatch(setMainDomain(null)); // Set main domain to null if no valid main domain is detected
            dispatch(setIsValidMainDomain(false)); // Set main domain validity to false if no valid main domain is detected
            dispatch(setSubdomain(null)); // Set subdomain to null if no valid subdomain is detected
            dispatch(setIsValidSubDomain(false)); // Set subdomain validity to false if no valid subdomain is detected
            dispatch(setDomainType('custom')); // Set domain type to root
        } else if (isIP) {
            StorageService.set('domain', mainDomain);
            StorageService.set('domain-type', 'main');
            dispatch(setDomain(mainDomain)); // Set domain
            //   console.log("IP address is valid.");
            dispatch(setMainDomain(mainDomain)); // Set main domain
            dispatch(setIsValidMainDomain(true)); // Set main domain validity to true
            dispatch(setSubdomain(null)); // Set subdomain to null if no valid subdomain is detected
            dispatch(setIsValidSubDomain(false)); // Set subdomain validity to false if no valid subdomain is detected
            dispatch(setCustomDomain(null)); // Set custom domain validity to true
            dispatch(setIsValidCustomDomain(false)); // Set custom domain validity to true
            dispatch(setDomainType('main')); // Set domain type to root
            dispatch(setPageLoading(true)); // Set page loading to true after fetching the logo and footer text
        }
        // If none of the conditions are met, set all to false
    }, [dispatch, mainDomain, mainParts, footerText]); // Add mainDomain and mainParts to the dependency array
    // Ensure that the effect runs when these values change
    return <>{children}</>; // Return the children components to be rendered
}
